import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { LogoMark } from "@/components/Logo";

/* ------------------------------------------------------------------ */
/*  Debii Social Brand Kit — internal design preview (not in app nav)  */
/*  Uses the REAL Debii logo, brand colours and typography.            */
/* ------------------------------------------------------------------ */

const B = {
  teal: "#0D4736",
  tealDark: "#0A382A",
  tealLight: "#155E49",
  sage: "#E8F0EC",
  emerald: "#10B981",
  mint: "#34D399",
  mintSoft: "#6EE7B7",
  softMint: "#D1FAE5",
  appBg: "#F6F8F7",
  panel: "#EEF3F0",
  card: "#FFFFFF",
  ink: "#111827",
  ink2: "#4B5563",
  muted: "#6B7280",
  line: "#E2E8E4",
  white: "#FFFFFF",
};
const DISPLAY = "'Outfit', sans-serif";
const BODY = "'Plus Jakarta Sans', sans-serif";

const Wordmark = ({ size, color = B.white }) => (
  <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: size, color, letterSpacing: "-0.02em" }}>
    Debii
  </span>
);

const Watermark = ({ dark }) => (
  <div style={{ position: "absolute", bottom: dark ? 46 : 50, left: 0, right: 0, textAlign: "center" }}>
    <span
      style={{
        fontFamily: BODY,
        fontWeight: 600,
        fontSize: 26,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: dark ? "rgba(255,255,255,0.62)" : "rgba(17,24,39,0.45)",
      }}
    >
      debiiapp.com
    </span>
  </div>
);

/* Small crisp naira line for demo cards */
const Naira = ({ children, color = B.ink, size }) => (
  <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: size, color, letterSpacing: "-0.01em" }}>
    {children}
  </span>
);

/* ============================ DESIGN 1 ============================ */
function MeetDebii({ w, h, square }) {
  const pad = square ? 108 : 128;
  return (
    <div style={{ width: w, height: h, background: B.teal, position: "relative", overflow: "hidden", fontFamily: BODY }}>
      <div style={{ position: "absolute", top: -h * 0.32, right: -w * 0.12, width: h * 0.9, height: h * 0.9, borderRadius: "50%", background: "radial-gradient(circle, rgba(52,211,153,0.30), transparent 62%)" }} />
      <div style={{ position: "absolute", bottom: -h * 0.3, left: -w * 0.1, width: h * 0.7, height: h * 0.7, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.16), transparent 65%)" }} />
      <div style={{ position: "absolute", inset: 0, padding: pad, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 22, marginBottom: square ? 40 : 44 }}>
          <LogoMark size={square ? 110 : 104} />
          <Wordmark size={square ? 72 : 68} />
        </div>
        <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: square ? 176 : 196, lineHeight: 0.98, color: B.white, letterSpacing: "-0.035em" }}>
          Meet Debii.
        </div>
        <div style={{ marginTop: square ? 36 : 34, fontSize: square ? 46 : 48, lineHeight: 1.28, color: B.mintSoft, fontWeight: 500, maxWidth: square ? 860 : 1040 }}>
          Keep track of who owes you<br />and who you owe.
        </div>
      </div>
      <Watermark dark />
    </div>
  );
}

/* ============================ DESIGN 2 ============================ */
function NoteChip({ text, rot, style }) {
  return (
    <div
      style={{
        position: "absolute",
        transform: `rotate(${rot}deg)`,
        background: B.white,
        border: `2px dashed ${B.line}`,
        borderRadius: 18,
        padding: "22px 30px",
        fontFamily: BODY,
        fontWeight: 600,
        fontSize: 34,
        color: B.muted,
        boxShadow: "0 18px 40px -18px rgba(17,24,39,0.25)",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {text}
    </div>
  );
}

function WhoOwes({ w, h, square }) {
  const pad = square ? 100 : 120;
  const CleanCard = ({ x, y, rot = 0, scale = 1 }) => (
    <div style={{ position: "absolute", left: x, top: y, transform: `rotate(${rot}deg) scale(${scale})`, transformOrigin: "center", width: 560, background: B.white, borderRadius: 28, padding: "30px 34px", boxShadow: "0 40px 80px -30px rgba(13,71,54,0.4)", display: "flex", alignItems: "center", gap: 26 }}>
      <div style={{ width: 76, height: 76, borderRadius: "50%", background: B.sage, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: DISPLAY, fontWeight: 800, fontSize: 36, color: B.teal }}>B</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 40, color: B.ink }}>Bola</div>
        <div style={{ marginTop: 10, display: "inline-block", background: B.sage, color: B.teal, fontFamily: BODY, fontWeight: 700, fontSize: 24, padding: "6px 16px", borderRadius: 999 }}>Outstanding</div>
      </div>
      <Naira color={B.teal} size={46}>₦30,000</Naira>
    </div>
  );
  return (
    <div style={{ width: w, height: h, background: B.panel, position: "relative", overflow: "hidden", fontFamily: BODY }}>
      {/* logo top-left */}
      <div style={{ position: "absolute", top: pad * 0.62, left: pad, display: "flex", alignItems: "center", gap: 16 }}>
        <LogoMark size={64} />
        <Wordmark size={44} color={B.teal} />
      </div>

      {square ? (
        <>
          <div style={{ position: "absolute", top: 210, left: 0, right: 0, height: 360 }}>
            <NoteChip text="Uche — ₦5k?" rot={-7} style={{ left: 80, top: 0 }} />
            <NoteChip text="office guy · 2k" rot={6} style={{ right: 80, top: 34 }} />
            <NoteChip text="matches money 🤔" rot={-3} style={{ left: 140, top: 140 }} />
            <CleanCard x={w / 2 - 250} y={224} rot={2} scale={0.88} />
          </div>
          <div style={{ position: "absolute", left: pad, right: pad, bottom: 128 }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 110, lineHeight: 0.98, color: B.ink, letterSpacing: "-0.03em" }}>Who owes you<br />again?</div>
            <div style={{ marginTop: 26, fontSize: 42, color: B.ink2, fontWeight: 500 }}>No more forgotten debts or scattered notes.</div>
          </div>
        </>
      ) : (
        <>
          <div style={{ position: "absolute", left: pad, top: h * 0.34, width: w * 0.5 }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 132, lineHeight: 0.96, color: B.ink, letterSpacing: "-0.03em" }}>Who owes<br />you again?</div>
            <div style={{ marginTop: 34, fontSize: 46, color: B.ink2, fontWeight: 500, maxWidth: 640 }}>No more forgotten debts or scattered notes.</div>
          </div>
          <div style={{ position: "absolute", right: 40, top: 90, width: w * 0.46, height: h - 180 }}>
            <NoteChip text="Uche — ₦5k?" rot={-8} style={{ left: 60, top: 40 }} />
            <NoteChip text="office guy · 2k" rot={7} style={{ right: 30, top: 20 }} />
            <NoteChip text="matches money 🤔" rot={-3} style={{ left: 40, top: 180 }} />
            <CleanCard x={70} y={330} rot={2} />
          </div>
        </>
      )}
      <Watermark />
    </div>
  );
}

/* ============================ DESIGN 3 ============================ */
function Phone({ frameH }) {
  const frameW = frameH * (430 / 932);
  return (
    <div style={{ width: frameW, height: frameH, background: "#0A2A20", borderRadius: frameH * 0.085, padding: frameH * 0.012, boxShadow: "0 60px 110px -40px rgba(13,71,54,0.55), 0 0 0 3px rgba(255,255,255,0.06)" }}>
      <img src="/brandkit/dashboard.png" alt="Debii dashboard" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", borderRadius: frameH * 0.072, display: "block" }} />
    </div>
  );
}
function Pill({ children, style }) {
  return (
    <div style={{ position: "absolute", background: B.white, borderRadius: 999, padding: "16px 26px", fontFamily: BODY, fontWeight: 700, fontSize: 30, color: B.teal, boxShadow: "0 20px 44px -18px rgba(13,71,54,0.5)", display: "flex", alignItems: "center", gap: 12, ...style }}>
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: B.mint }} />
      {children}
    </div>
  );
}
function Showcase({ w, h, square }) {
  const pad = square ? 96 : 120;
  return (
    <div style={{ width: w, height: h, background: `linear-gradient(155deg, ${B.tealLight} 0%, ${B.teal} 45%, ${B.tealDark} 100%)`, position: "relative", overflow: "hidden", fontFamily: BODY }}>
      <div style={{ position: "absolute", top: -h * 0.3, left: -w * 0.05, width: h * 0.8, height: h * 0.8, borderRadius: "50%", background: "radial-gradient(circle, rgba(52,211,153,0.22), transparent 62%)" }} />
      {square ? (
        <>
          <div style={{ position: "absolute", top: pad, left: pad, right: pad }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 26 }}>
              <LogoMark size={64} /><Wordmark size={44} />
            </div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 96, lineHeight: 1.0, color: B.white, letterSpacing: "-0.03em" }}>All your debts.<br />One clear view.</div>
          </div>
          <div style={{ position: "absolute", bottom: -90, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
            <div style={{ position: "relative" }}>
              <Phone frameH={h * 0.62} />
              <Pill style={{ top: h * 0.13, left: -150 }}>Clear totals</Pill>
              <Pill style={{ top: h * 0.30, right: -140 }}>Every record</Pill>
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ position: "absolute", left: pad, top: 0, bottom: 0, width: w * 0.5, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 40 }}>
              <LogoMark size={72} /><Wordmark size={52} />
            </div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 108, lineHeight: 1.0, color: B.white, letterSpacing: "-0.03em" }}>All your debts.<br />One clear view.</div>
            <div style={{ marginTop: 34, fontSize: 44, color: B.mintSoft, fontWeight: 500, maxWidth: 620 }}>See what's outstanding at a glance — clear balances, every record in one place.</div>
          </div>
          <div style={{ position: "absolute", right: 150, top: 0, bottom: 0, display: "flex", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <Phone frameH={h * 0.88} />
              <Pill style={{ top: h * 0.2, left: -200 }}>Clear totals</Pill>
              <Pill style={{ bottom: h * 0.22, right: -150 }}>Every record</Pill>
            </div>
          </div>
        </>
      )}
      <Watermark dark />
    </div>
  );
}

/* ============================ DESIGN 4 ============================ */
function BellIcon({ size = 40, color = B.teal }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function ReminderCard({ scale = 1 }) {
  const NAT_H = 616;
  const Chip = ({ label, active }) => (
    <div style={{ padding: "16px 30px", borderRadius: 999, fontFamily: BODY, fontWeight: 700, fontSize: 30, background: active ? B.teal : B.white, color: active ? B.white : B.ink2, border: active ? "none" : `2px solid ${B.line}` }}>{label}</div>
  );
  return (
    <div style={{ width: 720 * scale, height: NAT_H * scale }}>
      <div style={{ width: 720, transformOrigin: "top left", transform: `scale(${scale})`, background: B.white, borderRadius: 40, padding: 48, boxShadow: "0 60px 120px -40px rgba(13,71,54,0.45)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 34 }}>
        <div style={{ width: 72, height: 72, borderRadius: 20, background: B.softMint, display: "flex", alignItems: "center", justifyContent: "center" }}><BellIcon size={40} /></div>
        <div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 38, color: B.ink }}>Reminder for Bola</div>
          <div style={{ fontFamily: BODY, fontSize: 26, color: B.muted, marginTop: 4 }}>Pick a tone that sounds like you</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 30 }}>
        <Chip label="Friendly" /><Chip label="Firm" /><Chip label="Playful" active />
      </div>
      <div style={{ background: B.softMint, borderRadius: 28, borderTopLeftRadius: 8, padding: "30px 34px", marginBottom: 30 }}>
        <div style={{ fontFamily: BODY, fontWeight: 600, fontSize: 40, color: B.teal, lineHeight: 1.3 }}>Abeg, no forget my money 😅</div>
      </div>
      <div style={{ background: B.teal, borderRadius: 22, padding: "26px 0", textAlign: "center", fontFamily: DISPLAY, fontWeight: 700, fontSize: 34, color: B.white }}>Copy &amp; share it yourself</div>
      <div style={{ marginTop: 26, fontFamily: BODY, fontSize: 26, color: B.muted, textAlign: "center", lineHeight: 1.4 }}>You send it — Debii never messages anyone for you.</div>
      </div>
    </div>
  );
}
function Reminders({ w, h, square }) {
  const pad = square ? 96 : 118;
  return (
    <div style={{ width: w, height: h, background: B.appBg, position: "relative", overflow: "hidden", fontFamily: BODY }}>
      <div style={{ position: "absolute", top: -h * 0.28, right: -w * 0.06, width: h * 0.85, height: h * 0.85, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.16), transparent 64%)" }} />
      {square ? (
        <>
          <div style={{ position: "absolute", top: pad, left: pad, right: pad }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
              <LogoMark size={62} /><Wordmark size={42} color={B.teal} />
            </div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 84, lineHeight: 1.02, color: B.ink, letterSpacing: "-0.03em" }}>Abeg, no forget<br />my money 😅</div>
            <div style={{ marginTop: 20, fontSize: 40, color: B.ink2, fontWeight: 500 }}>Create a reminder. Choose your tone. Send it yourself.</div>
          </div>
          <div style={{ position: "absolute", bottom: 104, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
            <ReminderCard scale={0.68} />
          </div>
        </>
      ) : (
        <>
          <div style={{ position: "absolute", left: pad, top: 0, bottom: 0, width: w * 0.48, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 36 }}>
              <LogoMark size={68} /><Wordmark size={48} color={B.teal} />
            </div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 104, lineHeight: 1.0, color: B.ink, letterSpacing: "-0.03em" }}>Abeg, no<br />forget my<br />money 😅</div>
            <div style={{ marginTop: 32, fontSize: 44, color: B.ink2, fontWeight: 500, maxWidth: 640 }}>Create a reminder. Choose your tone. Send it yourself.</div>
          </div>
          <div style={{ position: "absolute", right: 130, top: 0, bottom: 0, display: "flex", alignItems: "center" }}>
            <ReminderCard scale={0.98} />
          </div>
        </>
      )}
      <Watermark />
    </div>
  );
}

/* ============================ DESIGN 5 ============================ */
function BrandValue({ w, h }) {
  return (
    <div style={{ width: w, height: h, background: B.teal, position: "relative", overflow: "hidden", fontFamily: BODY }}>
      <div style={{ position: "absolute", top: -h * 0.35, left: "50%", transform: "translateX(-50%)", width: h * 1.1, height: h * 1.1, borderRadius: "50%", background: "radial-gradient(circle, rgba(52,211,153,0.20), transparent 60%)" }} />
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 120 }}>
        <LogoMark size={h > 1000 ? 132 : 120} />
        <div style={{ marginTop: h > 1000 ? 60 : 56, fontFamily: DISPLAY, fontWeight: 800, fontSize: h > 1000 ? 148 : 156, lineHeight: 1.0, color: B.white, letterSpacing: "-0.035em", textAlign: "center" }}>
          Keep track.<br />Stay on top.
        </div>
        <div style={{ width: 120, height: 8, borderRadius: 999, background: B.mint, margin: "48px 0" }} />
        <div style={{ fontSize: h > 1000 ? 48 : 50, color: B.mintSoft, fontWeight: 500, textAlign: "center", maxWidth: 1100, lineHeight: 1.3 }}>
          A simpler way to manage personal debts.
        </div>
      </div>
      <Watermark dark />
    </div>
  );
}

/* ============================ PAGE ============================ */
const DESIGNS = [
  { id: "01-meet-debii", name: "Brand Introduction", C: MeetDebii },
  { id: "02-who-owes-you", name: "The Everyday Problem", C: WhoOwes },
  { id: "03-one-clear-view", name: "Product Showcase", C: Showcase },
  { id: "04-reminders", name: "Reminders", C: Reminders },
  { id: "05-brand-value", name: "Brand Value", C: BrandValue },
];
const FORMATS = [
  { key: "x", label: "X / LinkedIn / Facebook · 1600×900", w: 1600, h: 900, previewW: 600 },
  { key: "ig", label: "Instagram · 1080×1080", w: 1080, h: 1080, previewW: 420 },
];

export default function BrandKit() {
  const refs = useRef({});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const capture = async (key) => {
    const node = refs.current[key];
    await document.fonts.ready;
    await new Promise((r) => setTimeout(r, 120));
    return toPng(node, { width: node.offsetWidth, height: node.offsetHeight, pixelRatio: 1, cacheBust: true });
  };

  const downloadOne = async (d, f) => {
    const key = `${d.id}__${f.key}`;
    setBusy(true);
    setStatus(`Rendering ${d.name} (${f.key})…`);
    try {
      const url = await capture(key);
      const blob = await (await fetch(url)).blob();
      saveAs(blob, `debii-${d.id}-${f.w}x${f.h}.png`);
      setStatus("");
    } catch (e) {
      setStatus("Export failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const downloadAll = async () => {
    setBusy(true);
    const zip = new JSZip();
    try {
      for (const d of DESIGNS) {
        for (const f of FORMATS) {
          setStatus(`Rendering ${d.name} · ${f.w}×${f.h}…`);
          const url = await capture(`${d.id}__${f.key}`);
          const base64 = url.split(",")[1];
          zip.file(`debii-${d.id}-${f.w}x${f.h}.png`, base64, { base64: true });
        }
      }
      setStatus("Zipping…");
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "debii-social-brand-kit.zip");
      setStatus("Done — 10 images exported.");
    } catch (e) {
      setStatus("ZIP export failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0b0f0d", color: "#fff", fontFamily: BODY, paddingBottom: 120 }}>
      {/* Toolbar */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(10,15,13,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "18px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LogoMark size={40} />
          <div>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 22 }}>Debii — Social Brand Kit</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>5 designs · 10 images · internal preview</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {status && <span style={{ fontSize: 13, color: B.mintSoft }}>{status}</span>}
          <button
            data-testid="download-all-zip"
            onClick={downloadAll}
            disabled={busy}
            style={{ background: B.mint, color: B.tealDark, border: "none", borderRadius: 999, padding: "12px 24px", fontFamily: DISPLAY, fontWeight: 800, fontSize: 15, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "Working…" : "Download all (ZIP)"}
          </button>
        </div>
      </div>

      {/* Designs */}
      <div style={{ maxWidth: 1360, margin: "0 auto", padding: "36px 28px" }}>
        {DESIGNS.map((d, i) => (
          <section key={d.id} data-testid={`design-${d.id}`} style={{ marginBottom: 64 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 20 }}>
              <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 28, color: B.mint }}>{String(i + 1).padStart(2, "0")}</span>
              <h2 style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, margin: 0 }}>{d.name}</h2>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
              {FORMATS.map((f) => {
                const key = `${d.id}__${f.key}`;
                const scale = f.previewW / f.w;
                return (
                  <div key={f.key}>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 8, letterSpacing: "0.04em" }}>{f.label}</div>
                    {/* Preview is scaled on the WRAPPER only; the captured node
                        (ref) stays untransformed at native px so exports fill edge-to-edge. */}
                    <div style={{ width: f.w * scale, height: f.h * scale, overflow: "hidden", borderRadius: 14, boxShadow: "0 20px 50px -20px rgba(0,0,0,0.6)" }}>
                      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: f.w, height: f.h }}>
                        <div ref={(el) => (refs.current[key] = el)} style={{ width: f.w, height: f.h }}>
                          <d.C w={f.w} h={f.h} square={f.key === "ig"} />
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`download-${d.id}-${f.key}`}
                      onClick={() => downloadOne(d, f)}
                      disabled={busy}
                      style={{ marginTop: 12, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 999, padding: "9px 18px", fontFamily: DISPLAY, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    >
                      ↓ Download PNG ({f.w}×{f.h})
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
